import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import AttendanceLogin from '../components/Login';
import EmployeeAttendanceLayout from '../../employee-attendance/components/EmployeeAttendanceLayout';
import ErrorBoundary from '../components/ErrorBoundary';

// Import components
import TutorDashboard from '../components/TutorDashboard';
import FacultyAdminDashboard from '../components/FacultyAdminDashboard';
import UnifiedDashboard from '../components/UnifiedDashboard';
import TodaySessions from '../components/TodaySessions';
import WeeklyCalendar from '../components/WeeklyCalendar';
import MarkAttendance from '../components/MarkAttendance';
import ViewAttendance from '../components/ViewAttendance';
import AttendanceHistory from '../components/AttendanceHistory';
import TutorAttendanceReports from '../components/TutorAttendanceReports';

// Faculty Admin Components
import TimetablesList from '../components/TimetablesList';
import ViewTimetable from '../components/ViewTimetable';
import CreateTimetable from '../components/CreateTimetable';
import EditTimetable from '../components/EditTimetable';
import FacultySessions from '../components/FacultySessions';
import FacultySessionDetails from '../components/FacultySessionDetails';
import TeacherSheetReport from '../components/TeacherSheetReport';
import SearchStudentMarks from '../components/SearchStudentMarks';
import TutorPortalAccessSetup from '../components/TutorPortalAccessSetup';
import ChangePassword from '../components/ChangePassword';

// Camera Booking
import CameraCalendar from '../components/camera/CameraCalendar';
import CameraRequestForm from '../components/camera/CameraRequestForm';
import MyCameraRequests from '../components/camera/MyCameraRequests';
import CameraMediaInbox from '../components/camera/CameraMediaInbox';
import { hasCameraFacultyRole, hasCameraMediaRole } from '../utils/cameraBookingUtils';

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */
function ProtectedRoute({ children, requiredRole = null }) {
  const { isAuthenticated, user, loading } = useAttendanceAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/attendance/login" replace />;
  }

  // Check role requirements if specified
  if (requiredRole === 'tutor' && !user?.is_tutor) {
    return <Navigate to="/attendance/dashboard" replace />;
  }

  if (requiredRole === 'faculty_admin' && !user?.is_faculty_admin) {
    return <Navigate to="/attendance/dashboard" replace />;
  }

  if (requiredRole === 'camera_faculty' && !hasCameraFacultyRole(user)) {
    return <Navigate to="/attendance/dashboard" replace />;
  }

  if (requiredRole === 'camera_media' && !hasCameraMediaRole(user)) {
    return <Navigate to="/attendance/dashboard" replace />;
  }

  if (
    requiredRole === 'camera_any' &&
    !hasCameraFacultyRole(user) &&
    !hasCameraMediaRole(user)
  ) {
    return <Navigate to="/attendance/dashboard" replace />;
  }

  return children;
}

/**
 * Role-based route that shows different dashboards based on user roles
 */
function DashboardRoute() {
  const { user } = useAttendanceAuth();

  if (!user) {
    return <Navigate to="/attendance/login" replace />;
  }

  // Dual role - show unified dashboard
  if (user.is_tutor && user.is_faculty_admin) {
    return <UnifiedDashboard />;
  }

  // Faculty admin only
  if (user.is_faculty_admin) {
    return <Navigate to="/attendance/faculty-admin/dashboard" replace />;
  }

  // Tutor only
  if (user.is_tutor) {
    return <Navigate to="/attendance/tutor/dashboard" replace />;
  }

  // Camera-only users (media team, or faculty with camera access but no other role)
  if (hasCameraFacultyRole(user) || hasCameraMediaRole(user)) {
    return <Navigate to="/attendance/camera/calendar" replace />;
  }

  // No valid role
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <p className="text-red-600">No valid role assigned. Contact administrator.</p>
      </div>
    </div>
  );
}

/**
 * Attendance Routes Component
 * All attendance-related routes
 */
function AttendanceRoutes() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<AttendanceLogin />} />

        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <EmployeeAttendanceLayout>
                <Routes>
                  {/* Main Dashboard - redirects based on role */}
                  <Route path="/dashboard" element={<DashboardRoute />} />

                  {/* Tutor Routes */}
                  <Route
                    path="/tutor/dashboard"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <TutorDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tutor/today"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <TodaySessions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tutor/calendar"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <WeeklyCalendar />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/tutor/mark/:sessionId"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <MarkAttendance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tutor/view/:sessionId"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <ViewAttendance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tutor/history"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <AttendanceHistory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tutor/reports"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <TutorAttendanceReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/tutor/change-password"
                    element={
                      <ProtectedRoute requiredRole="tutor">
                        <ChangePassword />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/change-password"
                    element={
                      <ProtectedRoute>
                        <ChangePassword />
                      </ProtectedRoute>
                    }
                  />

                  {/* Faculty Admin Routes */}
                  <Route
                    path="/faculty-admin/dashboard"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <FacultyAdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/timetables"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <TimetablesList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/timetable/create"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <CreateTimetable />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/timetable/:id"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <ViewTimetable />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/timetable/:id/edit"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <EditTimetable />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/sessions"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <FacultySessions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/session/:id"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <FacultySessionDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/mark/:sessionId"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <MarkAttendance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/attendance-stats"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <TutorAttendanceReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/subject-report"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <TutorAttendanceReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/teacher-sheet-report"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <TeacherSheetReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/search-student-marks"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <SearchStudentMarks />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/faculty-admin/tutor-portal-setup"
                    element={
                      <ProtectedRoute requiredRole="faculty_admin">
                        <TutorPortalAccessSetup />
                      </ProtectedRoute>
                    }
                  />

                  {/* Camera Booking */}
                  <Route
                    path="/camera/calendar"
                    element={
                      <ProtectedRoute requiredRole="camera_any">
                        <CameraCalendar />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/camera/request"
                    element={
                      <ProtectedRoute requiredRole="camera_any">
                        <CameraRequestForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/camera/my-requests"
                    element={
                      <ProtectedRoute requiredRole="camera_faculty">
                        <MyCameraRequests />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/camera/inbox"
                    element={
                      <ProtectedRoute requiredRole="camera_media">
                        <CameraMediaInbox />
                      </ProtectedRoute>
                    }
                  />

                  {/* Default redirect */}
                  <Route path="/" element={<Navigate to="/attendance/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/attendance/dashboard" replace />} />
                </Routes>
              </EmployeeAttendanceLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
}

export default AttendanceRoutes;
